import { useState } from 'react';
import { X, Info, Save, Plane } from 'lucide-react';
import { projectsService } from '../../services/projects';
import ProjectAdvancedConfig, {
  buildSmeConfig,
  smeConfigToAdvanced,
  type AdvancedConfigValue,
} from '../../components/project/ProjectAdvancedConfig';

// --- TIPOS ---
export interface FleetScore {
  ac_type: string;
  footprint: number;
  pushback: number;
  engine_out: number;
  wingspan: number;
  turn_time: number;
  procedures: number;
}

// Apron complexity matrix keyed to the ICAO Aerodrome Reference Code LETTER
// (Annex 14, by WINGSPAN) — NOT RECAT-EU wake categories (those drive the
// runway node / RunCap). Apron complexity scales with size, so the burden
// increases from Code A (smallest) to Code F (largest, e.g. A380).
export const INITIAL_FLEET_SCORES: FleetScore[] = [
  { ac_type: 'ICAO F (65-80 m)', footprint: 5, pushback: 5, engine_out: 5, wingspan: 5, turn_time: 5, procedures: 5 },
  { ac_type: 'ICAO E (52-65 m)', footprint: 5, pushback: 4, engine_out: 4, wingspan: 5, turn_time: 4, procedures: 4 },
  { ac_type: 'ICAO D (36-52 m)', footprint: 4, pushback: 4, engine_out: 3, wingspan: 4, turn_time: 4, procedures: 3 },
  { ac_type: 'ICAO C (24-36 m)', footprint: 3, pushback: 3, engine_out: 2, wingspan: 3, turn_time: 3, procedures: 2 },
  { ac_type: 'ICAO B (15-24 m)', footprint: 2, pushback: 2, engine_out: 1, wingspan: 2, turn_time: 2, procedures: 1 },
  { ac_type: 'ICAO A (<15 m)', footprint: 1, pushback: 1, engine_out: 1, wingspan: 1, turn_time: 1, procedures: 1 },
];

// --- EF (Experience Factor) — ATC population sample, canon Block 3.1 Meth. 1 ---
// UNIT-experience bands drive the EF (1.0-centered weights so EF ~0.6-1.5, no RAC
// inflation). Age / company seniority / sex are DESCRIPTIVE only (never in EF).
export const EF_EXPERIENCE_DEFAULTS: { label: string; weight: number }[] = [
  { label: '0-1', weight: 0.65 }, { label: '1-3', weight: 0.85 }, { label: '3-7', weight: 1.00 },
  { label: '7-10', weight: 1.15 }, { label: '10-15', weight: 1.30 }, { label: '15-20', weight: 1.40 },
  { label: '>20', weight: 1.50 },
];
export const EF_AGE_BANDS = ['18-22', '23-26', '27-30', '31-34', '35-38', '38-41', '42-45', '46-49', '50-53', '>53'];
export const EF_SENIORITY_BANDS = ['0-1', '1-3', '3-7', '7-10', '10-15', '15-20', '>20'];

interface EfSample {
  unit_experience: { label: string; count: number | ''; weight: number | '' }[];
  roles: { atco: number | ''; supervisor: number | ''; instructor: number | ''; other: number | '' };
  role_uplift: boolean;
  age: { label: string; count: number | '' }[];
  company_seniority: { label: string; count: number | '' }[];
  sex: { men: number | ''; women: number | '' };
}

const emptyEfSample = (): EfSample => ({
  unit_experience: EF_EXPERIENCE_DEFAULTS.map(b => ({ label: b.label, count: '', weight: b.weight })),
  roles: { atco: '', supervisor: '', instructor: '', other: '' },
  role_uplift: false,
  age: EF_AGE_BANDS.map(l => ({ label: l, count: '' })),
  company_seniority: EF_SENIORITY_BANDS.map(l => ({ label: l, count: '' })),
  sex: { men: '', women: '' },
});

/** Hydrate an EfSample from stored sme_parameters.ef_sample (defaults if absent). */
const efSampleFromSme = (sme: any): EfSample => {
  const base = emptyEfSample();
  const s = sme?.ef_sample;
  if (!s) return base;
  const mergeCounts = (defaults: { label: string; count: number | '' }[], stored: any[]) =>
    defaults.map(d => {
      const hit = (stored || []).find((x: any) => x.label === d.label);
      return { ...d, count: hit && hit.count != null ? hit.count : '' };
    });
  return {
    unit_experience: EF_EXPERIENCE_DEFAULTS.map(b => {
      const hit = (s.unit_experience || []).find((x: any) => x.label === b.label);
      return { label: b.label, count: hit && hit.count != null ? hit.count : '', weight: hit && hit.weight != null ? hit.weight : b.weight };
    }),
    roles: {
      atco: s.roles?.atco ?? '', supervisor: s.roles?.supervisor ?? '',
      instructor: s.roles?.instructor ?? '', other: s.roles?.other ?? '',
    },
    role_uplift: !!s.role_uplift,
    age: mergeCounts(base.age, s.age),
    company_seniority: mergeCounts(base.company_seniority, s.company_seniority),
    sex: { men: s.sex?.men ?? '', women: s.sex?.women ?? '' },
  };
};

/** Live EF preview — mirrors backend apcas_logic.compute_ef_from_sample. */
const calcEfPreview = (s: EfSample): { ef: number; base: number; uplift: number; n: number } | null => {
  let num = 0, den = 0;
  for (const b of s.unit_experience) {
    const c = Number(b.count) || 0;
    if (c <= 0) continue;
    const wDef = EF_EXPERIENCE_DEFAULTS.find(d => d.label === b.label)?.weight ?? 1;
    const w = b.weight === '' || b.weight == null ? wDef : Number(b.weight);
    num += c * w; den += c;
  }
  if (den <= 0) return null;
  const base = num / den;
  let uplift = 1;
  if (s.role_uplift) {
    const senior = (Number(s.roles.supervisor) || 0) + (Number(s.roles.instructor) || 0);
    const totalRoles = (Number(s.roles.atco) || 0) + senior + (Number(s.roles.other) || 0);
    const bt = totalRoles > 0 ? totalRoles : den;
    if (bt > 0 && senior > 0) uplift = 1 + Math.min(0.12, 0.20 * (senior / bt));
  }
  const ef = Math.max(0.5, Math.min(2.0, base * uplift));
  return { ef, base, uplift, n: den };
};

// --- MODAL DE CONFIGURACIÓN (DETAILED CONFIG) ---
const ProjectConfigurationModal = ({ isOpen, onClose, project, isDark, onUpdate, language, fleetScores, handleScoreChange }: any) => {
  if (!isOpen || !project) return null;
  const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  const inputBg = isDark ? 'bg-[var(--surface-raised)] border-[var(--border)] text-[var(--text-primary)]' : 'bg-gray-50 border-gray-300 text-gray-900';
  const labelStyle = `text-xs font-bold block mb-1 uppercase tracking-wide ${'text-[var(--text-secondary)]'}`;

  // AirCap terminal config (editable post-creation), hydrated from the stored
  // sme_parameters.aircap_config. Terminal-only here (showAdvancedParams=false):
  // the CF weights are owned by this modal's own section below, so on save we
  // merge back ONLY the aircap_config fragment to avoid a double source.
  const [advConfig, setAdvConfig] = useState<AdvancedConfigValue>(() => smeConfigToAdvanced(project.sme_parameters));

  // EF (Experience Factor) — ATC population sample (canon Block 3.1 Methodology 1).
  const [efSample, setEfSample] = useState<EfSample>(() => efSampleFromSme(project.sme_parameters));
  const efPreview = calcEfPreview(efSample);

  // Local state for configuration
  const [formData, setFormData] = useState({
    // D-FOL INPUTS (Manual from field observations)
    w_workload: project.sme_parameters?.w_workload || 2.74,  // Workload in min/op (from D-FOL)
    n_dynamic: project.sme_parameters?.n_dynamic || 1.5,     // Dynamic concurrency (from D-FOL)
    t_sector: project.sme_parameters?.t_sector || 12.5,       // Sector time in minutes

    // APCAS Options
    cmp_infra_mode: project.sme_parameters?.cmp_infra_mode || 'AUTO',

    // Workload Calculation Method
    w_calc_method: project.sme_parameters?.w_calc_method || 'ROLLING_WINDOW',

    // Rolling Window - Window & Counts
    rolling_window_h: project.sme_parameters?.rolling_window_h || 10,
    rolling_ac_count: project.sme_parameters?.rolling_ac_count || 5,
    rolling_sv_count: project.sme_parameters?.rolling_sv_count || 2,

    // Rolling Window - Aircraft Processing
    ac_instr_count: project.sme_parameters?.ac_instr_count || 10,
    ac_instr_sec: project.sme_parameters?.ac_instr_sec || 30,
    ac_pushback_min: project.sme_parameters?.ac_pushback_min || 5,
    ac_startup_min: project.sme_parameters?.ac_startup_min || 2,
    ac_taxi_min: project.sme_parameters?.ac_taxi_min || 10,

    // Rolling Window - Vehicle Processing
    sv_instr_count: project.sme_parameters?.sv_instr_count || 4,
    sv_instr_sec: project.sme_parameters?.sv_instr_sec || 15,

    // EWMA Parameters
    ewma_w_hat: project.sme_parameters?.ewma_w_hat || 5.4,
    ewma_tau: project.sme_parameters?.ewma_tau || 10,
    ewma_delta_t: project.sme_parameters?.ewma_delta_t || 1,

    mean_sector_time: project.sme_parameters?.MEAN_SECTOR_TIME_MINS || 12.5,

    // APCAS Baseline
    controller_availability_pct: project.sme_parameters?.controller_availability_pct || 40, // Phi baseline = 0.40
    phi_calibrated: project.sme_parameters?.phi_calibrated || '', // legacy (prod path lo ignora); back-compat
    phi_source: project.sme_parameters?.phi_source || 'faa_tower', // measured|faa_tower|capan|faa_staffing|schmidt|manual
    phi_availability: project.sme_parameters?.phi_availability ?? '', // solo cuando phi_source=manual (fracción activa 0-1)
    cwl_target: project.sme_parameters?.cwl_target ?? 0.70, // Utilisation ceiling (Umbral SDP): 0.60-0.80, default 0.70
    planned_occupancy_pct: project.sme_parameters?.planned_occupancy_pct || 91, // FOC = 0.91
    rrc_capacity: project.sme_parameters?.RRC_CAPACITY || 30, // Runway Capacity

    // PTBC - Cálculo escalonado: PAX anuales → PHP (C_T) → PTBC_mov
    pax_anuales: project.sme_parameters?.pax_anuales || project.total_pax_last_year || '', // PAX anuales (fallback al campo top-level del proyecto)
    php_calc_method: project.sme_parameters?.php_calc_method || 'FAA', // Método FAA o AENA
    terminal_capacity_pax_hr: project.sme_parameters?.terminal_capacity_pax_hr || '', // C_T manual override
    load_factor_pct: project.sme_parameters?.load_factor_pct || 85, // LF: Factor de carga (85% típico)

    // Weights (0.0 - 1.0)
    weight_wcf: project.sme_parameters?.weight_wcf || 0.35,
    weight_nof: project.sme_parameters?.weight_nof || 0.15,
    weight_wif: project.sme_parameters?.weight_wif || 0.20,
    weight_tcf: project.sme_parameters?.weight_tcf || 0.30,
    weight_ccf: project.sme_parameters?.weight_ccf || 0.10,

    // CCF Manual Input (m² sin cobertura CNS / superficie total plataforma)
    // CCF — CNS coverage GAP split by Communications and Surveillance (Nav has no
    // apron impact, excluded). Each as m² without coverage OR a direct % (shared
    // toggle). Surveillance dominates in the backend (0.7 surv / 0.3 comms).
    ccf_input_mode: (project.sme_parameters?.ccf_surv_pct != null || project.sme_parameters?.ccf_comms_pct != null) ? 'pct' : 'm2',
    ccf_surv: (project.sme_parameters?.ccf_surv_pct != null
      ? (Number(project.sme_parameters.ccf_surv_pct) <= 1 ? Number(project.sme_parameters.ccf_surv_pct) * 100 : Number(project.sme_parameters.ccf_surv_pct))
      : (project.sme_parameters?.ccf_surv_m2 ?? '')),
    ccf_comms: (project.sme_parameters?.ccf_comms_pct != null
      ? (Number(project.sme_parameters.ccf_comms_pct) <= 1 ? Number(project.sme_parameters.ccf_comms_pct) * 100 : Number(project.sme_parameters.ccf_comms_pct))
      : (project.sme_parameters?.ccf_comms_m2 ?? '')),

    // Manual Efficiency Factors
    manual_ef: project.sme_parameters?.experience_factor || 1.35,
    has_acdm: project.sme_parameters?.has_acdm || false,
    manual_acdm_ef: project.sme_parameters?.acdm_efficiency_factor || 1.0,


    // ADOSA - DSR Teórico
    num_apron_towers: project.sme_parameters?.num_apron_towers || 1,
    num_op_positions: project.sme_parameters?.num_op_positions || 2,
    has_supervisor_position: project.sme_parameters?.has_supervisor_position || false,
    schedule_hours: project.sme_parameters?.schedule_hours || 24,
    num_shifts: project.sme_parameters?.num_shifts || 3,
    resting_factor_pct: project.sme_parameters?.resting_factor_pct || 25,

    // ADOSA - Staffing Tool
    roster_work_days: project.sme_parameters?.DIAS_TRABAJADOS_CICLO_SME || 6,
    roster_off_days: project.sme_parameters?.DIAS_LIBRES_CICLO_SME || 4,
    holidays_vacation_days: project.sme_parameters?.holidays_vacation_days || 30,
    bank_holidays_days: project.sme_parameters?.bank_holidays_days || 14,
    work_permits_days: project.sme_parameters?.work_permits_days || 5,
    absenteeism_pct: project.sme_parameters?.absenteeism_pct || 5,
    yearly_training_hours: project.sme_parameters?.yearly_training_hours || 20,
    training_shift_duration: project.sme_parameters?.training_shift_duration || 8,
    safety_buffer_pct: project.sme_parameters?.safety_buffer_pct || 5,
    is_calendar_days: project.sme_parameters?.is_calendar_days || false,
    real_guard_dsr: project.sme_parameters?.real_guard_dsr || 0,
  });

  // Separate state for shift types table
  const [shiftTypes, setShiftTypes] = useState<Array<{ shift_name: string, hours_per_shift: number, staff_per_shift: number }>>(
    project.sme_parameters?.shift_types || [
      { shift_name: 'Mañana', hours_per_shift: 8, staff_per_shift: 2 },
      { shift_name: 'Tarde', hours_per_shift: 8, staff_per_shift: 2 },
      { shift_name: 'Noche', hours_per_shift: 8, staff_per_shift: 2 }
    ]
  );

  const handleChange = (field: string, val: any) => {
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  // --- LIVE PREVIEW LOGIC ---
  const live = (() => {
    const numTowers = parseFloat(formData.num_apron_towers) || 0;
    const numOpPos = parseFloat(formData.num_op_positions) || 0;
    const hasSup = formData.has_supervisor_position;
    const numShifts = parseFloat(formData.num_shifts) || 0;

    // DSR
    const posPerTower = numOpPos + (hasSup ? 1 : 0);
    const dsrTheoretic = numTowers * posPerTower * numShifts;
    const dsrReal = hasSup ? dsrTheoretic : dsrTheoretic + (1 * numShifts);

    // Productivity / Availability
    const workDays = parseFloat(formData.roster_work_days) || 0;
    const offDays = parseFloat(formData.roster_off_days) || 0;
    const pattern = workDays + offDays;
    const holidays = parseFloat(formData.holidays_vacation_days) || 0;
    const absenteeism = parseFloat(formData.absenteeism_pct) || 0;

    const bankHols = parseFloat(formData.bank_holidays_days) || 0;
    const permits = parseFloat(formData.work_permits_days) || 0;

    const cyclesPerYear = pattern > 0 ? 365 / pattern : 0;
    const availableDays = cyclesPerYear * workDays;

    let effectiveHols = holidays;
    if (formData.is_calendar_days && pattern > 0) {
      effectiveHols = holidays * (workDays / pattern);
    }

    const netDutyDays = (availableDays - (effectiveHols + bankHols + permits)) - (availableDays * absenteeism / 100);

    return {
      dsrTheoretic: dsrTheoretic.toFixed(1),
      dsrReal: dsrReal.toFixed(1),
      netDutyDays: Math.max(0, netDutyDays).toFixed(1)
    };
  })();

  const handleSave = async () => {
    // Validación de campos obligatorios
    if (!formData.cmp_infra_mode || formData.cmp_infra_mode === 'AUTO') {
      alert("Por favor, selecciona una metodología de capacidad (Mirkovic o Simplificado) en la columna AP-CAM.");
      return;
    }

    // Map form data back to sme_parameters structure expected by backend
    // Helper to safely parse floats (avoid NaN)
    const safeFloat = (val: any, def: number = 0) => {
      const n = parseFloat(val);
      return isNaN(n) ? def : n;
    };

    const updatedSme = {
      ...project.sme_parameters,
      cmp_infra_mode: formData.cmp_infra_mode,

      // D-FOL INPUTS (Critical for APCAS calculation)
      w_workload: safeFloat(formData.w_workload, 2.74),
      n_dynamic: safeFloat(formData.n_dynamic, 1.5),
      t_sector: safeFloat(formData.t_sector, 12.5),

      // Workload method and parameters
      w_calc_method: formData.w_calc_method,
      rolling_window_h: safeFloat(formData.rolling_window_h, 10),
      rolling_ac_count: safeFloat(formData.rolling_ac_count, 5),
      rolling_sv_count: safeFloat(formData.rolling_sv_count, 0),

      ac_instr_count: safeFloat(formData.ac_instr_count, 10),
      ac_instr_sec: safeFloat(formData.ac_instr_sec, 30),
      ac_pushback_min: safeFloat(formData.ac_pushback_min, 5),
      ac_startup_min: safeFloat(formData.ac_startup_min, 2),
      ac_taxi_min: safeFloat(formData.ac_taxi_min, 10),

      sv_instr_count: safeFloat(formData.sv_instr_count, 0),
      sv_instr_sec: safeFloat(formData.sv_instr_sec, 0),

      ewma_w_hat: safeFloat(formData.ewma_w_hat, 5.4),
      ewma_tau: safeFloat(formData.ewma_tau, 10),
      ewma_delta_t: safeFloat(formData.ewma_delta_t, 1),

      // Sync t_sector with mean_sector_time
      MEAN_SECTOR_TIME_MINS: safeFloat(formData.t_sector, 12.5),

      // APCAS Baseline
      controller_availability_pct: safeFloat(formData.controller_availability_pct, 40), // legacy, prod path lo ignora
      phi_calibrated: formData.phi_calibrated ? safeFloat(formData.phi_calibrated) : null, // legacy back-compat
      phi_source: formData.phi_source || 'faa_tower',
      phi_availability: formData.phi_source === 'manual' ? safeFloat(formData.phi_availability, 0.60) : null,
      cwl_target: safeFloat(formData.cwl_target, 0.70),
      planned_occupancy_pct: safeFloat(formData.planned_occupancy_pct, 91),
      // RRC is NOT editable here — RunCap computes effRRC from the datasheet RECAT
      // mix (block7). Any existing RRC_CAPACITY is preserved via the spread above;
      // we no longer overwrite it from a manual field.

      // PTBC - Cálculo: PAX anuales → PHP (C_T) → PTBC_mov
      pax_anuales: formData.pax_anuales ? safeFloat(formData.pax_anuales) : null,
      php_calc_method: formData.php_calc_method, // 'FAA' o 'AENA'
      terminal_capacity_pax_hr: formData.terminal_capacity_pax_hr ? safeFloat(formData.terminal_capacity_pax_hr) : null,
      load_factor_pct: safeFloat(formData.load_factor_pct, 85),

      weight_wcf: safeFloat(formData.weight_wcf, 0.35),
      weight_nof: safeFloat(formData.weight_nof, 0.15),
      weight_wif: safeFloat(formData.weight_wif, 0.20),
      weight_tcf: safeFloat(formData.weight_tcf, 0.30),
      weight_ccf: safeFloat(formData.weight_ccf, 0.10),
      // CCF gap split by Communications and Surveillance (Nav excluded). Send the
      // active unit (% or m²) per line and clear the other; % stored as 0-1.
      ccf_surv_pct: formData.ccf_input_mode === 'pct' && formData.ccf_surv !== '' ? safeFloat(formData.ccf_surv) / 100 : null,
      ccf_comms_pct: formData.ccf_input_mode === 'pct' && formData.ccf_comms !== '' ? safeFloat(formData.ccf_comms) / 100 : null,
      ccf_surv_m2: formData.ccf_input_mode === 'm2' && formData.ccf_surv !== '' ? safeFloat(formData.ccf_surv) : null,
      ccf_comms_m2: formData.ccf_input_mode === 'm2' && formData.ccf_comms !== '' ? safeFloat(formData.ccf_comms) : null,
      // Legacy single-gap fields no longer written from the UI.
      ccf_value: null,
      ccf_pct: null,

      // EF comes from the ATC population sample (backend recomputes from ef_sample
      // when present). Persist the effective value for consistency; fall back to the
      // stored value or the 1.35 default when no sample is provided.
      experience_factor: efPreview ? efPreview.ef : safeFloat(project.sme_parameters?.experience_factor, 1.35),
      has_acdm: formData.has_acdm,
      acdm_efficiency_factor: safeFloat(formData.manual_acdm_ef, 1.0),

      // ADOSA - DSR Teórico
      num_apron_towers: safeFloat(formData.num_apron_towers, 1),
      num_op_positions: safeFloat(formData.num_op_positions, 2),
      has_supervisor_position: formData.has_supervisor_position,
      schedule_hours: safeFloat(formData.schedule_hours, 24),
      num_shifts: safeFloat(formData.num_shifts, 3),
      resting_factor_pct: safeFloat(formData.resting_factor_pct, 25),

      // ADOSA - Staffing Tool
      DIAS_TRABAJADOS_CICLO_SME: safeFloat(formData.roster_work_days, 6),
      DIAS_LIBRES_CICLO_SME: safeFloat(formData.roster_off_days, 4),
      holidays_vacation_days: safeFloat(formData.holidays_vacation_days, 30),
      bank_holidays_days: safeFloat(formData.bank_holidays_days, 14),
      work_permits_days: safeFloat(formData.work_permits_days, 5),
      absenteeism_pct: safeFloat(formData.absenteeism_pct, 5),
      yearly_training_hours: safeFloat(formData.yearly_training_hours, 20),
      training_shift_duration: safeFloat(formData.training_shift_duration, 8),
      safety_buffer_pct: safeFloat(formData.safety_buffer_pct, 5),
      is_calendar_days: formData.is_calendar_days,
      real_guard_dsr: safeFloat(formData.real_guard_dsr, 0),

      // Shift types configuration
      shift_types: shiftTypes,
    };

    // Merge the (editable) AirCap terminal config back in. We take ONLY the
    // aircap_config fragment from buildSmeConfig — the CF/IPS/v_ref/bank params
    // it can also emit are owned by this modal elsewhere (showAdvancedParams is
    // false on the card), so they are not touched here.
    const _terminalFrag = buildSmeConfig(advConfig) as { aircap_config?: unknown };
    if (_terminalFrag.aircap_config) {
      (updatedSme as Record<string, unknown>).aircap_config = _terminalFrag.aircap_config;
    }

    // EF population sample — persisted so the backend computes EF_weighted
    // (canon Block 3.1). If it carries any unit-experience headcount, the
    // computed EF supersedes the manual experience_factor scalar.
    (updatedSme as Record<string, unknown>).ef_sample = efSample;

    try {
      await projectsService.update(project.id, { sme_parameters: updatedSme });
      onUpdate();
      onClose();
      // Show success message with reminder
      setTimeout(() => {
        alert(language === 'es'
          ? 'Configuración guardada correctamente.\n\nPara aplicar los cambios, pulse "Re-Run Analysis" .'
          : 'Configuration saved successfully.\n\nTo apply changes, click "Re-Run Analysis" .');
      }, 100);
    } catch (error) {
      console.error("Error updating configuration:", error);
      alert(language === 'es' ? "Error al guardar la configuración." : "Error saving configuration.");
    }
  };

  return (
    <div className="fixed inset-0 left-80 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className={`w-full max-w-5xl rounded-xl border shadow-2xl p-6 md:p-8 my-8 max-h-[90vh] overflow-y-auto ${cardBg}`}>
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-[var(--border)]/50">
          <div>
            <h3 className={`text-2xl font-bold ${textMain}`}>
              {language === 'es' ? 'Configuración Operativa' : 'Operational Configuration'}
            </h3>
            <p className={`text-sm ${'text-[var(--text-secondary)]'}`}>
              {language === 'es'
                ? 'Ajuste de parámetros de Workload, AP-CAM y Staffing (ADOSA).'
                : 'Workload, AP-CAM and Staffing (ADOSA) parameters adjustment.'}
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* 2-COLUMN GRID (Staffing Tool movido a tab ADOSA) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {/* UNIFIED: D-FOL + CAPACIDAD INFRA (APCAS) */}
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase text-[#F26430]">
                  {language === 'es' ? '1. Capacidad y Workload' : '1. Capacity & Workload'}
                </h4>
                <div className="group relative">
                  <Info size={14} className="text-[var(--text-secondary)] cursor-help" />
                  <div className={`absolute left-0 bottom-full mb-2 w-72 p-3 rounded-lg shadow-xl text-xs leading-relaxed z-[110] invisible group-hover:visible ${cardBg} border border-[var(--border)]`}>
                    <strong>D-FOL:</strong> {language === 'es' ? 'Valores obtenidos del estudio de campo.' : 'Values from field observation study.'}<br />
                    <strong>AP-CAM:</strong> {language === 'es' ? 'Configuración de capacidad de infraestructura.' : 'Infrastructure capacity configuration.'}<br />
                    <em>{language === 'es' ? 'W es el driver principal para la Capacidad Real (RAC).' : 'W is the main driver for Real Capacity (RAC).'}</em>
                  </div>
                </div>
              </div>

              {/* D-FOL INPUTS (from field observations) - PRIMARY */}
              <div className="mb-4">
                <p className="text-xs font-bold text-orange-400 mb-3 uppercase flex items-center gap-2">
                  <span className="bg-orange-500/20 px-2 py-0.5 rounded">D-FOL</span>
                  {language === 'es' ? 'Inputs from Field Observations' : 'Inputs from Field Observations'}
                </p>
                <p className={`text-xs ${'text-[var(--text-secondary)]'} mb-3`}>
                  {language === 'es'
                    ? 'Estos valores vienen del estudio de campo D-FOL y son críticos para el cálculo de capacidad'
                    : 'These values come from the D-FOL field study and are critical for capacity calculation'}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelStyle}>W Workload (min/op)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.5"
                      max="20"
                      className={`w-full p-2 rounded border outline-none ${inputBg} border-orange-500/30`}
                      value={formData.w_workload}
                      onChange={e => handleChange('w_workload', parseFloat(e.target.value) || 2.74)}
                    />
                    <p className="text-xs text-orange-500 mt-1">
                      {language === 'es' ? 'Carga de trabajo por operación' : 'Workload per operation'}
                    </p>
                  </div>
                  <div>
                    <label className={labelStyle}>N_dynamic</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="10"
                      className={`w-full p-2 rounded border outline-none ${inputBg} border-orange-500/30`}
                      value={formData.n_dynamic}
                      onChange={e => handleChange('n_dynamic', parseFloat(e.target.value) || 1.5)}
                    />
                    <p className="text-xs text-orange-500 mt-1">
                      {language === 'es' ? 'Aeronaves manejables simultáneamente (D-FOL) — rango típico 1–5' : 'Aircraft handleable simultaneously (D-FOL) — typical range 1–5'}
                    </p>
                  </div>
                  <div>
                    <label className={labelStyle}>T_sector (min)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="1"
                      max="60"
                      className={`w-full p-2 rounded border outline-none ${inputBg} border-orange-500/30`}
                      value={formData.t_sector}
                      onChange={e => handleChange('t_sector', parseFloat(e.target.value) || 12.5)}
                    />
                    <p className="text-xs text-orange-500 mt-1">
                      {language === 'es' ? 'Tiempo medio de sector' : 'Average sector time'}
                    </p>
                  </div>
                </div>
              </div>

              {/* CMPinfra Mode Selector */}
              <div className="mb-4 pt-4 border-t border-[var(--border)]/30">
                <label className={labelStyle}>
                  {language === 'es' ? 'Método CMP_infra' : 'CMP_infra Method'}
                </label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    onClick={() => handleChange('cmp_infra_mode', 'OPTION_A')}
                    className={`p-2 text-xs rounded border transition-all ${formData.cmp_infra_mode === 'OPTION_A' ? 'bg-[#F26430] border-[#F26430] text-white font-bold' : 'border-[var(--border)] hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]'}`}
                  >
                    Mirkovic
                  </button>
                  <button
                    onClick={() => handleChange('cmp_infra_mode', 'OPTION_B')}
                    className={`p-2 text-xs rounded border transition-all ${formData.cmp_infra_mode === 'OPTION_B' ? 'bg-[#F26430] border-[#F26430] text-white font-bold' : 'border-[var(--border)] hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]'}`}
                  >
                    {language === 'es' ? 'Método DACAMS' : 'DACAMS Method'}
                  </button>
                </div>

                {/* Info Card */}
                <div className="mt-3 p-3 rounded-lg border border-blue-500/20 bg-blue-500/5 text-xs leading-relaxed">
                  {formData.cmp_infra_mode === 'OPTION_A' ? (
                    <div>
                      <p className="font-semibold text-blue-400 mb-1">Mirkovic (Enhanced)</p>
                      <p className="text-[var(--text-secondary)]">
                        {language === 'es'
                          ? 'Agrupa los stands por categoría ICAO (C/D/E/F) y calcula el TAT por clase de aeronave desde el AODB. La capacidad queda limitada por la clase más restrictiva (bottleneck). Ideal cuando hay mezcla heterogénea de flota (narrow + wide body).'
                          : 'Groups stands by ICAO category (C/D/E/F) and computes TAT per aircraft class from AODB. Capacity is limited by the most constrained class (bottleneck). Best for heterogeneous fleets (narrow + wide body mix).'}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-semibold text-blue-400 mb-1">{language === 'es' ? 'Método DACAMS' : 'DACAMS Method'}</p>
                      <p className="text-[var(--text-secondary)]">
                        {language === 'es'
                          ? 'Fórmula canónica: CMP = N × (1-Foc) / (T_schedule + T_buffer − δ_tech). Usa conteo total de stands, TAT medio del AODB y buffer de variabilidad. Ideal para flotas homogéneas o cuando no hay datos de clasificación de stands.'
                          : 'Canonical formula: CMP = N × (1-Foc) / (T_schedule + T_buffer − δ_tech). Uses total stand count, average AODB TAT and variability buffer. Best for homogeneous fleets or when stand classification data is unavailable.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* APCAS Baseline */}
              <div className="pt-4 border-t border-[var(--border)]/30">
                <p className="text-xs font-bold text-blue-400 mb-3 uppercase">AP-CAM Baseline</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <label className={labelStyle}>{language === 'es' ? 'Φ — Fuente' : 'Φ — Source'}</label>
                      <div className="group relative">
                        <Info size={13} className="text-[var(--text-secondary)] cursor-help" />
                        <div className={`absolute left-0 bottom-full mb-2 w-80 p-3 rounded-lg shadow-xl text-xs leading-relaxed z-[110] invisible group-hover:visible ${cardBg} border border-[var(--border)]`}>
                          {language === 'es'
                            ? <>Φ = fracción de <strong>ocupación activa</strong> sostenible (1−Φ = reserva de vigilancia visual). <strong>Medido</strong> = del D-FOL (N_dyn·W/T, requiere concurrencia real). <strong>FAA-torre 0.60</strong> (plataforma, 40% reserva visual) · <strong>CAPAN 0.70</strong> · <strong>FAA-staffing 0.75</strong> · <strong>Schmidt 0.80</strong>. El histórico 0.40 era la reserva, no el activo.</>
                            : <>Φ = sustainable <strong>active-occupancy</strong> fraction (1−Φ = visual-scan reserve). <strong>Measured</strong> = from D-FOL (N_dyn·W/T, needs real concurrency). <strong>FAA-tower 0.60</strong> (apron, 40% visual reserve) · <strong>CAPAN 0.70</strong> · <strong>FAA-staffing 0.75</strong> · <strong>Schmidt 0.80</strong>. The legacy 0.40 was the reserve, not the active fraction.</>}
                        </div>
                      </div>
                    </div>
                    <select
                      className={`w-full p-2 rounded border outline-none ${inputBg}`}
                      value={formData.phi_source}
                      onChange={e => handleChange('phi_source', e.target.value)}
                    >
                      <option value="measured">{language === 'es' ? 'Medido (D-FOL: N_dyn·W/T)' : 'Measured (D-FOL: N_dyn·W/T)'}</option>
                      <option value="faa_tower">FAA-torre — 0.60</option>
                      <option value="capan">CAPAN — 0.70</option>
                      <option value="faa_staffing">FAA-staffing — 0.75</option>
                      <option value="schmidt">Schmidt — 0.80</option>
                      <option value="manual">{language === 'es' ? 'Manual' : 'Manual'}</option>
                    </select>
                    <p className="text-xs text-[var(--text-tertiary)] mt-1">
                      {language === 'es' ? 'phi_empirical (N_dyn·W/T) se reporta como benchmark en resultados' : 'phi_empirical (N_dyn·W/T) reported as a benchmark in results'}
                    </p>
                  </div>
                  <div>
                    <label className={labelStyle}>{language === 'es' ? 'Φ manual (0.40–0.85)' : 'Manual Φ (0.40–0.85)'}</label>
                    <input
                      type="number"
                      step="0.05"
                      min="0.40"
                      max="0.85"
                      disabled={formData.phi_source !== 'manual'}
                      placeholder={formData.phi_source === 'manual' ? '0.60' : (language === 'es' ? 'Solo si Fuente = Manual' : 'Only if Source = Manual')}
                      className={`w-full p-2 rounded border outline-none ${inputBg} ${formData.phi_source !== 'manual' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      value={formData.phi_availability ?? ''}
                      onChange={e => handleChange('phi_availability', e.target.value)}
                    />
                    <p className="text-xs text-emerald-500 mt-1">
                      {language === 'es' ? 'Fracción activa; su complemento es la reserva visual' : 'Active fraction; its complement is the visual reserve'}
                    </p>
                  </div>
                  <div>
                    <label className={labelStyle}>FOC (Occupancy %)</label>
                    <input type="number" step="1" min="50" max="100" className={`w-full p-2 rounded border outline-none ${inputBg}`} value={formData.planned_occupancy_pct} onChange={e => handleChange('planned_occupancy_pct', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelStyle}>RRC (Runway mov/hr)</label>
                    <div className={`w-full p-2 rounded border ${inputBg} opacity-70 text-xs flex items-center`} style={{ minHeight: '2.375rem' }}>
                      {language === 'es' ? 'Calculado por RunCap (mix RECAT del datasheet)' : 'Computed by RunCap (datasheet RECAT mix)'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <label className={labelStyle}>
                        {language === 'es' ? 'Techo de utilización — CWL_target (Umbral SDP)' : 'Utilisation ceiling — CWL_target (SDP threshold)'}
                      </label>
                      <div className="group relative">
                        <Info size={13} className="text-[var(--text-secondary)] cursor-help" />
                        <div className={`absolute left-0 bottom-full mb-2 w-80 p-3 rounded-lg shadow-xl text-xs leading-relaxed z-[110] invisible group-hover:visible ${cardBg} border border-[var(--border)]`}>
                          {language === 'es'
                            ? <>Fracción máxima de la hora que un controlador sostiene en <strong>trabajo activo</strong> antes de sobrecarga. Es el umbral que decide <strong>cuándo se abre una nueva posición</strong> y por tanto el nº de posiciones (NOP) y la plantilla.<br /><strong>0.80</strong> (FAA-Schmidt): menos posiciones, menos colchón de seguridad.<br /><strong>0.70</strong> (CAPAN): estándar conservador en ruta/sector complejo.<br /><strong>0.60</strong> (plataforma): máxima reserva para vigilancia visual e imprevistos, más posiciones.<br /><em>Elige según el margen de seguridad exigido por el regulador.</em></>
                            : <>Maximum fraction of the hour a controller sustains in <strong>active work</strong> before overload. It is the threshold that decides <strong>when a new position opens</strong>, hence the number of positions (NOP) and staffing.<br /><strong>0.80</strong> (FAA-Schmidt): fewer positions, less safety buffer.<br /><strong>0.70</strong> (CAPAN): conservative en-route/complex-sector standard.<br /><strong>0.60</strong> (apron): maximum reserve for visual scan and contingencies, more positions.<br /><em>Choose per the safety margin required by the regulator.</em></>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="range"
                        min={0.60}
                        max={0.80}
                        step={0.05}
                        value={formData.cwl_target}
                        onChange={e => handleChange('cwl_target', e.target.value)}
                        className="flex-1 accent-[#F26430] cursor-pointer"
                      />
                      <span className="text-sm font-mono font-bold text-[#F26430] w-12 text-right">
                        {Math.round(Number(formData.cwl_target) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
              <h4 className="text-sm font-bold mb-4 uppercase text-[#F26430]">
                {language === 'es' ? '2. PTBC y Equilibrio' : '2. PTBC & Equilibrium'}
              </h4>

              {/* PTBC - Cálculo escalonado: PAX → PHP → PTBC */}
              <div>
                <p className="text-xs font-bold text-cyan-400 mb-2 uppercase">
                  {language === 'es' ? 'PTBC (Equilibrio Trinodal)' : 'PTBC (Tri-nodal Equilibrium)'}
                </p>

                {/* Flujo explicativo */}
                <div className={`p-2 rounded-lg text-xs mb-3 ${isDark ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  <strong>{language === 'es' ? 'Flujo' : 'Flow'}:</strong> {language === 'es' ? 'PAX anuales' : 'Annual PAX'} → C<sub>T</sub> (PHP) → PTBC<sub>mov</sub><br />
                  C<sub>T</sub> = PAX × Coef(FAA/AENA) | PTBC = C<sub>T</sub> / P̄
                </div>

                {/* Fila 1: PAX anuales y Método */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className={labelStyle}>
                      {language === 'es' ? 'PAX Anuales *' : 'Annual PAX *'}
                    </label>
                    <input
                      type="number"
                      step="100000"
                      placeholder={language === 'es' ? 'Ej: 5000000' : 'E.g.: 5000000'}
                      className={`w-full p-2 rounded border outline-none ${inputBg}`}
                      value={formData.pax_anuales || ''}
                      onChange={e => handleChange('pax_anuales', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelStyle}>
                      {language === 'es' ? 'Método Cálculo' : 'Calculation Method'}
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleChange('php_calc_method', 'FAA')}
                        className={`flex-1 p-2 text-xs rounded border transition-all ${formData.php_calc_method === 'FAA' ? 'bg-[#F26430] border-[#F26430] text-white font-bold' : 'border-[var(--border)] hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]'}`}
                      >
                        FAA
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChange('php_calc_method', 'AENA')}
                        className={`flex-1 p-2 text-xs rounded border transition-all ${formData.php_calc_method === 'AENA' ? 'bg-[#F26430] border-[#F26430] text-white font-bold' : 'border-[var(--border)] hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]'}`}
                      >
                        AENA
                      </button>
                    </div>
                  </div>
                </div>

                {/* Cálculo PHP en tiempo real */}
                {formData.pax_anuales && (() => {
                  const pax = parseFloat(formData.pax_anuales);
                  let coef = 0.00045, php = 0;
                  if (formData.php_calc_method === 'FAA') {
                    if (pax >= 20000000) coef = 0.0003;
                    else if (pax >= 10000000) coef = 0.00035;
                    else if (pax >= 1000000) coef = 0.00045;
                    else if (pax >= 500000) coef = 0.0005;
                    else coef = 0.00065;
                    php = pax * coef;
                  } else {
                    const Y = 105.62 * Math.pow(pax, -0.4723);
                    php = pax * (Y / 100);
                  }
                  return (
                    <div className={`p-2 rounded-lg mb-3 ${isDark ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                      <p className="text-sm font-mono text-amber-400">
                        C<sub>T</sub> (PHP) = {php.toFixed(0)} pax/hr
                        <span className="text-[var(--text-secondary)] ml-2">
                          ({formData.php_calc_method}: {formData.php_calc_method === 'FAA' ? `coef=${(coef * 100).toFixed(3)}%` : (language === 'es' ? 'fórmula log' : 'log formula')})
                        </span>
                      </p>
                    </div>
                  );
                })()}

                {/* Fila 2: Override manual y LF */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelStyle}>
                      C<sub>T</sub> Override ({language === 'es' ? 'opcional' : 'optional'})
                    </label>
                    <input type="number" step="100" placeholder={language === 'es' ? 'Auto-calculado' : 'Auto-calculated'} className={`w-full p-2 rounded border outline-none ${inputBg}`} value={formData.terminal_capacity_pax_hr || ''} onChange={e => handleChange('terminal_capacity_pax_hr', e.target.value)} />
                    <p className="text-xs text-[var(--text-tertiary)] mt-1">
                      {language === 'es' ? 'Sobrescribir cálculo FAA/AENA' : 'Override FAA/AENA calculation'}
                    </p>
                  </div>
                  <div>
                    <label className={labelStyle}>LF (Load Factor %)</label>
                    <input type="number" step="1" min="50" max="100" className={`w-full p-2 rounded border outline-none ${inputBg}`} value={formData.load_factor_pct} onChange={e => handleChange('load_factor_pct', e.target.value)} />
                    <p className="text-xs text-[var(--text-tertiary)] mt-1">
                      {language === 'es' ? 'Para cálculo de P„' : 'For P̄ calculation'}
                    </p>
                  </div>
                </div>

                <div className={`mt-3 p-2 rounded-lg text-xs ${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
                  PTBC<sub>mov</sub> = C<sub>T</sub> / P̄ → {language === 'es' ? 'Calculado durante análisis usando mix de flota' : 'Calculated during analysis using fleet mix'}
                </div>
              </div>

              {/* AirCap terminal config (editable) — the detailed PTBC inputs
                  (inventory, LoS, pax split, ops factors, pax/mov) hydrated from
                  sme_parameters.aircap_config. Blank fields fall back to IATA ADRM. */}
              <div className="mt-4">
                <ProjectAdvancedConfig
                  value={advConfig}
                  onChange={setAdvConfig}
                  language={language}
                  showAdvancedParams={false}
                />
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
              <h4 className="text-sm font-bold mb-4 uppercase text-[#F26430]">
                {language === 'es' ? '3. Complejidad y Pesos' : '3. Complexity & Weights'}
              </h4>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { k: 'weight_wcf', l: 'Weather (WCF)' }, { k: 'weight_nof', l: 'Night Ops (NOF)' },
                  { k: 'weight_wif', l: 'Works (WIF)' }, { k: 'weight_tcf', l: 'Taxiway (TCF)' },
                  { k: 'weight_ccf', l: 'CNS (CCF)' }
                ].map(f => (
                  <div key={f.k}>
                    <label className={labelStyle}>{f.l}</label>
                    <input type="number" step="0.05" min="0" max="1" className={`w-full p-2 rounded border outline-none ${inputBg}`} value={formData[f.k as keyof typeof formData]} onChange={e => handleChange(f.k, e.target.value)} />
                  </div>
                ))}
              </div>

              {/* CCF Manual Input — m² without CNS coverage OR direct % (toggle) */}
              <div className="pt-4 border-t border-[var(--border)]/30">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <label className={labelStyle}>
                    {language === 'es' ? 'CCF - Superficie sin cobertura CNS' : 'CCF - Area without CNS coverage'}
                  </label>
                  <div className="inline-flex rounded-lg border overflow-hidden border-[var(--border)]">
                    {(['m2', 'pct'] as const).map(m => (
                      <button key={m} type="button" onClick={() => handleChange('ccf_input_mode', m)}
                        className="px-3 py-1 text-xs font-semibold transition-colors"
                        style={{
                          background: formData.ccf_input_mode === m ? 'var(--accent-soft)' : 'transparent',
                          color: formData.ccf_input_mode === m ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                        }}>
                        {m === 'm2' ? 'm²' : '%'}
                      </button>
                    ))}
                  </div>
                </div>
                <p className={`text-xs ${'text-[var(--text-secondary)]'} mb-3`}>
                  {language === 'es'
                    ? 'Superficie SIN cobertura, separada por Vigilancia y Comunicaciones (en '
                    : 'Area WITHOUT coverage, split by Surveillance and Communications (in '}
                  {formData.ccf_input_mode === 'm2' ? 'm²' : '%'}
                  {language === 'es'
                    ? '). La vigilancia domina (0,7 / 0,3); la navegación no impacta en plataforma y se excluye.'
                    : '). Surveillance dominates (0.7 / 0.3); navigation has no apron impact and is excluded.'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelStyle}>
                      {language === 'es' ? 'Vigilancia' : 'Surveillance'} ({formData.ccf_input_mode === 'm2' ? 'm²' : '%'})
                    </label>
                    <input
                      type="number" min="0" step={formData.ccf_input_mode === 'm2' ? '100' : '1'}
                      max={formData.ccf_input_mode === 'm2' ? undefined : 100}
                      className={`w-full p-2 rounded border outline-none ${inputBg}`}
                      value={formData.ccf_surv}
                      onChange={e => handleChange('ccf_surv', e.target.value)}
                      placeholder={formData.ccf_input_mode === 'm2' ? 'Ej: 5000' : 'Ej: 100'}
                    />
                  </div>
                  <div>
                    <label className={labelStyle}>
                      {language === 'es' ? 'Comunicaciones' : 'Communications'} ({formData.ccf_input_mode === 'm2' ? 'm²' : '%'})
                    </label>
                    <input
                      type="number" min="0" step={formData.ccf_input_mode === 'm2' ? '100' : '1'}
                      max={formData.ccf_input_mode === 'm2' ? undefined : 100}
                      className={`w-full p-2 rounded border outline-none ${inputBg}`}
                      value={formData.ccf_comms}
                      onChange={e => handleChange('ccf_comms', e.target.value)}
                      placeholder={formData.ccf_input_mode === 'm2' ? 'Ej: 0' : 'Ej: 0'}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[var(--border)]/30 space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className={labelStyle}>Exp. Factor (EF)</label>
                    <div className={`w-full p-2 rounded border ${inputBg} flex items-center justify-between`} style={{ minHeight: '2.375rem' }}>
                      <span className="font-mono font-bold" style={{ color: efPreview ? 'var(--aipron-accent)' : 'var(--text-secondary)' }}>
                        {efPreview ? efPreview.ef.toFixed(3) : '1.35'}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        {efPreview
                          ? (language === 'es' ? 'de la muestra ATC ▾' : 'from ATC sample ▾')
                          : (language === 'es' ? 'defecto (sin muestra)' : 'default (no sample)')}
                      </span>
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                      {language === 'es'
                        ? 'Calculado en "Muestra de población ATC → EF" (abajo).'
                        : 'Computed in "ATC population sample → EF" (below).'}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-[var(--border)]/30">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer"
                        checked={formData.has_acdm}
                        onChange={e => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            has_acdm: checked,
                            manual_acdm_ef: checked ? prev.manual_acdm_ef : 1.0
                          }));
                        }}
                      />
                      <label className={`text-sm font-bold cursor-pointer ${textMain}`}>
                        {language === 'es' ? 'Implementación A-CDM' : 'A-CDM Implementation'}
                      </label>
                    </div>

                    <div>
                      <label className={labelStyle}>A-CDMEF Factor</label>
                      <input
                        type="number"
                        step="0.01"
                        disabled={!formData.has_acdm}
                        className={`w-full p-2 rounded border outline-none ${inputBg} ${!formData.has_acdm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        value={formData.manual_acdm_ef}
                        onChange={e => handleChange('manual_acdm_ef', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-[var(--text-tertiary)] italic">
                  {language === 'es'
                    ? 'SoT: A-CDMEF usualmente ≥ 1.0 (EF por defecto 1.35). Si no hay A-CDM, A-CDMEF = 1.0.'
                    : 'SoT: A-CDMEF usually ≥ 1.0 (default EF 1.35). If no A-CDM, A-CDMEF = 1.0.'}
                </p>
              </div>
            </div>
          </div>
          {/* END OF GRID - Staffing Tool movido a tab ADOSA */}
        </div> {/* Cierre del grid */}

        {/* ATC POPULATION SAMPLE -> EF (Experience Factor), canon Block 3.1 Meth.1 */}
        <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'bg-gray-800/30 border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
          <div className="flex items-center justify-between mb-1 gap-3">
            <h3 className={`text-sm font-bold ${textMain}`}>
              <Plane className="w-4 h-4 inline mr-1" />
              {language === 'es' ? 'Muestra de población ATC → EF' : 'ATC population sample → EF'}
            </h3>
            {efPreview && (
              <span className="text-xs font-bold px-3 py-1 rounded-lg" style={{ background: 'var(--accent-soft)', color: 'var(--aipron-accent)' }}>
                EF = {efPreview.ef.toFixed(3)}
                <span className="opacity-60 font-normal"> (base {efPreview.base.toFixed(3)}{efPreview.uplift !== 1 ? ` × ${efPreview.uplift.toFixed(3)}` : ''}, n={efPreview.n})</span>
              </span>
            )}
          </div>
          <p className={`text-xs mb-4 ${textSub}`}>
            {language === 'es'
              ? 'El EF sale de la EXPERIENCIA EN LA UNIDAD (ponderada). Edad, antigüedad en la empresa y sexo son descriptivos (perfil de plantilla), no entran en el EF. Si no rellenas la muestra, se usa el EF manual.'
              : 'EF is driven by UNIT experience (weighted). Age, company seniority and sex are descriptive (workforce profile), not part of EF. Leave the sample blank to use the manual EF.'}
          </p>

          {/* Unit experience (drives EF) + editable weights */}
          <p className="text-[11px] font-bold uppercase tracking-wider mb-2 text-[var(--aipron-accent)]">
            {language === 'es' ? 'Experiencia en la unidad (años) — dimensiona el EF' : 'Unit experience (years) — drives EF'}
          </p>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className={`px-2 py-1 text-left font-bold uppercase ${textSub}`}>{language === 'es' ? 'Bloque' : 'Band'}</th>
                  {efSample.unit_experience.map(b => (
                    <th key={b.label} className={`px-2 py-1 text-center font-bold ${textSub}`}>{b.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[var(--border)]/40">
                  <td className={`px-2 py-1 font-medium ${textMain}`}>{language === 'es' ? 'Nº ATCO' : '# ATCO'}</td>
                  {efSample.unit_experience.map((b, i) => (
                    <td key={b.label} className="px-1 py-1">
                      <input type="number" min="0" step="1" value={b.count}
                        onChange={e => setEfSample(s => { const ue = [...s.unit_experience]; ue[i] = { ...ue[i], count: e.target.value === '' ? '' : parseInt(e.target.value) }; return { ...s, unit_experience: ue }; })}
                        className={`w-14 p-1 text-center rounded border outline-none ${inputBg}`} placeholder="0" />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={`px-2 py-1 font-medium ${textSub}`}>{language === 'es' ? 'Peso' : 'Weight'}</td>
                  {efSample.unit_experience.map((b, i) => (
                    <td key={b.label} className="px-1 py-1">
                      <input type="number" step="0.05" value={b.weight}
                        onChange={e => setEfSample(s => { const ue = [...s.unit_experience]; ue[i] = { ...ue[i], weight: e.target.value === '' ? '' : parseFloat(e.target.value) }; return { ...s, unit_experience: ue }; })}
                        className={`w-14 p-1 text-center rounded border outline-none ${inputBg} opacity-80`} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Roles + optional uplift */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="w-full">
              <p className="text-[11px] font-bold uppercase tracking-wider mb-2 text-[var(--aipron-accent)]">{language === 'es' ? 'Roles' : 'Roles'}</p>
            </div>
            {([['atco', 'ATCO'], ['supervisor', 'Supervisor'], ['instructor', 'Instructor'], ['other', language === 'es' ? 'Otros' : 'Other']] as const).map(([k, l]) => (
              <div key={k}>
                <label className={labelStyle}>{l}</label>
                <input type="number" min="0" step="1" value={efSample.roles[k as keyof typeof efSample.roles]}
                  onChange={e => setEfSample(s => ({ ...s, roles: { ...s.roles, [k]: e.target.value === '' ? '' : parseInt(e.target.value) } }))}
                  className={`w-20 p-2 rounded border outline-none ${inputBg}`} placeholder="0" />
              </div>
            ))}
            <label className="flex items-center gap-2 text-xs cursor-pointer ml-2" style={{ color: 'var(--text-secondary)' }}>
              <input type="checkbox" className="w-4 h-4 cursor-pointer" checked={efSample.role_uplift}
                onChange={e => setEfSample(s => ({ ...s, role_uplift: e.target.checked }))} />
              {language === 'es' ? 'Uplift por instructores/supervisores (máx +12%)' : 'Uplift for instructors/supervisors (max +12%)'}
            </label>
          </div>

          {/* Descriptive profile: age / company seniority / sex (NOT in EF) */}
          <details className="text-xs">
            <summary className={`cursor-pointer font-semibold ${textSub} mb-2`}>
              {language === 'es' ? 'Perfil de plantilla (descriptivo — no afecta al EF)' : 'Workforce profile (descriptive — not part of EF)'}
            </summary>
            <div className="mt-3 space-y-4">
              {([['age', language === 'es' ? 'Edad (años)' : 'Age (years)', efSample.age] as const,
                 ['company_seniority', language === 'es' ? 'Antigüedad en la empresa (años)' : 'Company seniority (years)', efSample.company_seniority] as const]).map(([key, title, rows]) => (
                <div key={key} className="overflow-x-auto">
                  <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>{title}</p>
                  <table className="min-w-full">
                    <thead><tr className="border-b border-[var(--border)]/40">
                      {(rows as { label: string }[]).map(r => <th key={r.label} className={`px-1 py-0.5 text-center font-bold ${textSub}`}>{r.label}</th>)}
                    </tr></thead>
                    <tbody><tr>
                      {(rows as { label: string; count: number | '' }[]).map((r, i) => (
                        <td key={r.label} className="px-1 py-1">
                          <input type="number" min="0" step="1" value={r.count}
                            onChange={e => setEfSample(s => { const arr = [...(s as any)[key]]; arr[i] = { ...arr[i], count: e.target.value === '' ? '' : parseInt(e.target.value) }; return { ...s, [key]: arr }; })}
                            className={`w-12 p-1 text-center rounded border outline-none ${inputBg}`} placeholder="0" />
                        </td>
                      ))}
                    </tr></tbody>
                  </table>
                </div>
              ))}
              <div className="flex items-end gap-3">
                <p className={`text-[10px] font-bold uppercase tracking-wider ${textSub} w-full`}>{language === 'es' ? 'Sexo' : 'Sex'}</p>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Hombres' : 'Men'}</label>
                  <input type="number" min="0" step="1" value={efSample.sex.men}
                    onChange={e => setEfSample(s => ({ ...s, sex: { ...s.sex, men: e.target.value === '' ? '' : parseInt(e.target.value) } }))}
                    className={`w-20 p-2 rounded border outline-none ${inputBg}`} placeholder="0" />
                </div>
                <div>
                  <label className={labelStyle}>{language === 'es' ? 'Mujeres' : 'Women'}</label>
                  <input type="number" min="0" step="1" value={efSample.sex.women}
                    onChange={e => setEfSample(s => ({ ...s, sex: { ...s.sex, women: e.target.value === '' ? '' : parseInt(e.target.value) } }))}
                    className={`w-20 p-2 rounded border outline-none ${inputBg}`} placeholder="0" />
                </div>
              </div>
            </div>
          </details>
        </div>

        {/* ICAO ARC COMPLEXITY MATRIX (apron complexity by wingspan — NOT wake) */}
        <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'bg-gray-800/30 border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
          <h3 className={`text-sm font-bold mb-3 ${textMain}`}>
            <><Plane className="w-4 h-4 inline mr-1" />{language === 'es' ? 'Matriz de Complejidad ICAO ARC' : 'ICAO ARC Complexity Matrix'}</>
          </h3>
          <p className={`text-xs mb-3 ${textSub}`}>
            {language === 'es' ? 'Ponderación manual (0-5) por código de referencia ICAO (envergadura, Anexo 14)' : 'Manual weighting (0-5) per ICAO Aerodrome Reference Code (wingspan, Annex 14)'}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className={`border-b ${'border-[var(--border)]'}`}>
                  <th className={`px-3 py-2 text-left font-bold uppercase ${textSub}`}>{language === 'es' ? 'Categoría' : 'Category'}</th>
                  {['Footprint', 'Pushback', 'Engine Out', 'Wingspan', 'Turn Time', 'Procedures'].map(h => (
                    <th key={h} className={`px-3 py-2 text-center font-bold uppercase ${textSub}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {(fleetScores || INITIAL_FLEET_SCORES).map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td className={`px-3 py-2 font-medium ${textMain}`}>{row.ac_type}</td>
                    {['footprint', 'pushback', 'engine_out', 'wingspan', 'turn_time', 'procedures'].map(field => (
                      <td key={field} className="px-3 py-2 text-center">
                        <select
                          value={row[field]}
                          onChange={e => handleScoreChange && handleScoreChange(idx, field, e.target.value)}
                          className={`w-12 h-7 text-center text-xs font-bold rounded border transition-colors cursor-pointer appearance-none ${row[field] >= 4
                            ? 'bg-red-500/20 text-red-400 border-red-500/30 focus:border-red-400'
                            : row[field] >= 3
                              ? 'bg-orange-500/20 text-orange-400 border-orange-500/30 focus:border-orange-400'
                              : 'bg-green-500/20 text-green-400 border-green-500/30 focus:border-green-400'
                            } focus:outline-none focus:ring-1 focus:ring-current`}
                        >
                          {[1, 2, 3, 4, 5].map(v => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[var(--border)]/50">
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg font-bold transition-all border ${isDark ? 'border-[var(--border)] hover:bg-[var(--surface-raised)]' : 'border-gray-300 hover:bg-gray-50'} ${textMain}`}
          >
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            className="px-8 py-2 bg-gradient-to-r from-[#F26430] to-[#E85D04] hover:scale-105 active:scale-95 text-white rounded-lg font-bold shadow-lg transition-all flex items-center gap-2"
          >
            <Save size={18} /> {language === 'es' ? 'Guardar Configuración' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div >
  );
};

export default ProjectConfigurationModal;
